import type { VariableDefinition } from "@babackapp/variables-registry";
import type useAddWorkflowV2 from "@babackapp/private-api-client/client/use-add-workflow-v2";
import type useBulkDeleteWorkflowsV2 from "@babackapp/private-api-client/client/use-bulk-delete-workflows-v2";
import type useBulkUpdateWorkflowsV2 from "@babackapp/private-api-client/client/use-bulk-update-workflows-v2";
import type useDeleteWorkflowV2 from "@babackapp/private-api-client/client/use-delete-workflow-v2";
import type useGetAllWorkflowsV2 from "@babackapp/private-api-client/client/use-get-all-workflows-v2";
import type {
  invalidateAllWorkflowsV2Key,
  useGetAllWorkflowsV2Suspense,
} from "@babackapp/private-api-client/client/use-get-all-workflows-v2";
import type useGetAllWorkflowsV2Execution from "@babackapp/private-api-client/client/use-get-all-workflows-v2-execution";
import type useUpdateWorkflowV2 from "@babackapp/private-api-client/client/use-update-workflow-v2";
import type {
  useSendTestWorkflowWebhook,
  useWorkflowWebhookSample,
} from "@babackapp/private-api-client/client/use-workflow-webhooks";
import { createContext, type ReactNode, useContext } from "react";
import type { ConditionFieldDefinition } from "../nodes/condition/condition-config";
import type { EditorNodeType } from "../nodes";
import type { CreateWorkflowDto } from "../types";

export type WorkflowsEditorDomain = "tracking" | "return";

/**
 * API surface consumed by the editor core. Both backend engines share the
 * same graph JSON contract, so the core is backend-agnostic: each host
 * injects the hooks of its engine (workflows-v2 endpoints for tracking, the
 * dedicated return-workflows endpoints for return).
 *
 * Hook references MUST be stable module-level functions: the core calls them
 * unconditionally on every render (rules of hooks).
 */
export type WorkflowsApiAdapter = {
  useWorkflows: typeof useGetAllWorkflowsV2;
  /** Suspense variant consumed by the canvas data hook. */
  useWorkflowsSuspense: typeof useGetAllWorkflowsV2Suspense;
  useAddWorkflow: typeof useAddWorkflowV2;
  useUpdateWorkflow: typeof useUpdateWorkflowV2;
  useDeleteWorkflow: typeof useDeleteWorkflowV2;
  useBulkUpdateWorkflows: typeof useBulkUpdateWorkflowsV2;
  useBulkDeleteWorkflows: typeof useBulkDeleteWorkflowsV2;
  useWorkflowExecutions: typeof useGetAllWorkflowsV2Execution;
  /**
   * Query key of the workflows list — used for optimistic cache updates
   * (and their rollback) in the listing sidebar.
   */
  getWorkflowsQueryKey: typeof invalidateAllWorkflowsV2Key;
};

/**
 * Domain-agnostic condition field: the tracking ConditionFieldDefinition
 * with value/trigger types widened so return fields (returnRequest.* paths,
 * return_* triggers) fit the same slot. Derived from the base type so new
 * members stay in lockstep — note the deliberate coupling: tracking-only
 * members added to the base type will surface here and must make sense for
 * every domain.
 */
export type WorkflowsConditionField = Omit<
  ConditionFieldDefinition,
  "value" | "triggers"
> & {
  value: string;
  triggers: readonly string[];
};

/**
 * Per-domain configuration of the workflows editor, injected by the host —
 * same pattern as the templates editor's TemplatesEditorConfigContext. The
 * tracking host wires the current behavior (see tracking-provider.tsx); the
 * return host will provide its own config (BAB-2851/2852/2853/2854).
 */
export type WorkflowsEditorConfig = {
  domain: WorkflowsEditorDomain;

  api: WorkflowsApiAdapter;

  /** Trigger types offered by this domain (filters the create modal). */
  availableTriggerTypes: readonly string[];

  /**
   * Action node types offered in the add-node palette. The condition node is
   * always available. Hosts may further restrict at runtime (plan gating).
   */
  availableActionTypes: readonly EditorNodeType[];

  /**
   * Domain variables for the dictionary and node inspectors. Must be a
   * stable hook reference (called on every render by the core).
   */
  useVariables: () => VariableDefinition[];

  /** Variable ids available for a given trigger (others are greyed out). */
  getVariablesForTrigger: (triggerType: string) => readonly string[];

  /**
   * Every variable id of the domain's registry — a SUPERSET of the ids
   * returned by useVariables. The unavailable-variables set is computed
   * against this universe because it propagates into the embedded template
   * editor (email customization sheet), which renders template-only
   * variables the workflow dictionary does not display.
   */
  variableUniverse: readonly string[];

  /** Condition fields configurable in this domain. */
  conditionFields: ReadonlyArray<WorkflowsConditionField>;

  /**
   * eventData fields shown in the execution history sidebar (the engines
   * record domain-specific event shapes: status/orderId/trackingNumber for
   * tracking, returnRequestId/orderId for return).
   */
  executionEventFields: ReadonlyArray<{
    /** Key in execution.eventData. */
    key: string;
    /** trackoffice i18n key of the displayed label. */
    labelKey: string;
    defaultLabel: string;
  }>;

  /**
   * Zendesk ticket node: does the panel expose the custom fields editor?
   * Only the return engine forwards them to Zendesk (the tracking engine
   * hardcodes custom_fields: []), so tracking hides them.
   */
  supportsZendeskCustomFields: boolean;

  /**
   * Domain-specific fields merged into the workflow create body
   * (kind/origin for tracking; empty for return).
   */
  createDefaults: Partial<CreateWorkflowDto>;

  /**
   * "Send webhook" node: example payload and test delivery. Hosts without the
   * node leave it out. Stable module-level hooks, like `api`.
   */
  webhooks?: {
    useSample: typeof useWorkflowWebhookSample;
    useSendTest: typeof useSendTestWorkflowWebhook;
  };
};

const WorkflowsEditorConfigContext = createContext<
  WorkflowsEditorConfig | undefined
>(undefined);

export function WorkflowsEditorConfigProvider({
  config,
  children,
}: {
  config: WorkflowsEditorConfig;
  children: ReactNode;
}) {
  return (
    <WorkflowsEditorConfigContext.Provider value={config}>
      {children}
    </WorkflowsEditorConfigContext.Provider>
  );
}

export function useWorkflowsEditorConfig(): WorkflowsEditorConfig {
  const context = useContext(WorkflowsEditorConfigContext);

  if (context === undefined) {
    throw new Error(
      "useWorkflowsEditorConfig must be used within a WorkflowsEditorConfigProvider",
    );
  }
  return context;
}
